import { urlConstants } from "../common/constants/urlConstants";
import {
  adminEndPoints,
  nonAuthorizedApis,
} from "../common/constants/urlConstants";
import AbortService from "../services/abort";

const abortController = AbortService.getInstance();

// Token refresh management
let isTokenRefreshing = false;
let failedRequestsQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason: any) => void;
  request: () => Promise<any>;
}> = [];

const addToQueue = (resolve: any, reject: any, request: () => Promise<any>) => {
  failedRequestsQueue.push({ resolve, reject, request });
};

const processQueue = () => {
  failedRequestsQueue.forEach(({ resolve, request }) => {
    request()
      .then(resolve)
      .catch((error) => {
        console.error("Queued request failed:", error);
        resolve(null);
      });
  });
  failedRequestsQueue = [];
};

const generateToken = async (refreshToken: string, userId: string) => {
  try {
    const userData = localStorage.getItem("loggedInUser");
    const userType = userData ? JSON.parse(userData)?.typeOfUser?.[0] : "";

    const response = await fetch(
      userType === "admin"
        ? `${urlConstants?.BASE_URL}${nonAuthorizedApis.refreshTokenAdmin}`
        : `${urlConstants?.BASE_URL}${nonAuthorizedApis.refreshTokenUser}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refreshToken: refreshToken,
          userId: userId,
        }),
      }
    );

    if (response.ok) {
      const data = await response.json();
      return { status: response.status, data };
    } else {
      const errorData = await response.json();
      return { status: response.status, response: { data: errorData } };
    }
  } catch (error) {
    return { error };
  }
};

const responseHandler = async <T>(
  response: Response,
  originalRequest: () => Promise<Response>
): Promise<T | null> => {
  try {
    // Handle 401 errors with token refresh
    if (response.status === 401) {
      const refreshToken = localStorage.getItem("refreshToken");
      const userData = localStorage.getItem("loggedInUser");
      const userId = userData ? JSON.parse(userData)?.id : "";

      // Check if we should attempt token refresh
      if (refreshToken && userId && !response.url.includes("refreshToken")) {
        if (!isTokenRefreshing) {
          isTokenRefreshing = true;
          try {
            const tokenResponse = await generateToken(refreshToken, userId);
            if (tokenResponse?.status === 200) {
              const newAccessToken = tokenResponse?.data?.accessToken;
              localStorage.setItem("token", newAccessToken);
              processQueue(); // Retry failed requests
              // Retry the original request with new token
              const retryResponse = await originalRequest();
              return responseHandler<T>(retryResponse, originalRequest);
            } else {
              if (tokenResponse?.response?.data?.status === 9003) {
                localStorage.setItem("isSessionExpired", "true");
              }
              throw new Error(`HTTP error! status: ${response.status}`);
            }
          } catch (e: any) {
            if (e?.response?.data?.status === 9003) {
              localStorage.setItem("isSessionExpired", "true");
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          } finally {
            isTokenRefreshing = false;
          }
        } else {
          // If token is already being refreshed, queue this request
          return new Promise((resolve, reject) => {
            addToQueue(resolve, reject, originalRequest);
          });
        }
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    if (!response.ok) {
      throw new Error(
        `status: ${response.status}\n${(await response.json()).error_msg}`
      );
    }
    return response?.json().then((r) => {
      if (
        (r?.response?.success !== undefined && !r?.response?.success) ||
        (r?.success !== undefined && !r?.success)
      ) {
        throw r;
      }
      if (r.response !== undefined) {
        return r.response;
      }
      return r;
    });
  } catch (error) {
    console.error("Error processing response:", error);
    throw error;
  }
};

const callApi = async <T>(
  input: RequestInfo | URL,
  init?: RequestInit | undefined,
  abortKey?: string
): Promise<T | null> => {
  try {
    const url = `${urlConstants.BASE_URL}${input}`;
    const isNonAuthorizedApi = Object.values(nonAuthorizedApis).some((api) =>
      url.includes(api)
    );
    const isAdminEndpoint = Object.values(adminEndPoints).some((endpoint) =>
      url.includes(endpoint)
    );

    // Prepare headers
    let headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(init?.headers as Record<string, string>),
    };

    // Add authentication tokens for authorized APIs
    if (!isNonAuthorizedApi) {
      const storedToken = localStorage.getItem("token")?.replaceAll('"', "");
      if (storedToken) {
        headers.token = storedToken;
        headers.Authorization = `Bearer ${storedToken}`;
      }
    }

    // Add access code for admin endpoints
    if (isAdminEndpoint) {
      const accessCode = sessionStorage
        .getItem("accessCode")
        ?.replaceAll('"', "");
      if (accessCode) {
        headers.accessCode = accessCode;
      }
    }

    let options: RequestInit = {
      ...init,
      headers,
      signal: abortKey ? abortController.getSignal(abortKey) : undefined,
    };

    // Create a function to make the request (for retries)
    const makeRequest = async (): Promise<Response> => {
      // Refresh headers in case token was updated during retry
      const currentToken = localStorage.getItem("token")?.replaceAll('"', "");
      if (!isNonAuthorizedApi && currentToken) {
        options.headers = {
          ...options.headers,
          token: currentToken,
          Authorization: `Bearer ${currentToken}`,
        };
      }
      return fetch(url, options);
    };

    const response = await makeRequest();
    return await responseHandler<T>(response, makeRequest);
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      console.warn("Request aborted:", error);
      return null;
    }
    console.error("Error fetching API:", error);
    throw error;
  }
};

export default callApi;
